import React, { useEffect, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as IEditor } from "tinymce";
import { RawEditorOptions } from "tinymce";
import { ICustomCssModelv1 } from "api/files";
import Loading from "../loading";
import { TinyMceTemplatesService } from "modules/common/services/tinyMceTemplateService";
import { acceptableLanguages, spellcheckerRpcUrl, lcidToHunspellLcid } from "utils/spellcheckLanguages";
import { ospreyApi } from "api/instances";
import { IPersonalizationToken, MERGE_TAG_PREFIX, MERGE_TAG_SUFFIX } from "modules/emails/models";
import { userAttributesSvg } from "modules/common/icons/userAttributesSvgString";
import { magicIconWhiteSVG } from "modules/common/icons/magicIconWhiteSvgString";
import { PromptTypeEnum } from "modules/smartContent/models";
import { MENTIONS_MARK_HIGHLIGHT_STYLE } from "utils/generalConstants";

import "./styles/sparrowTinyMce.sass";
import { convertSVGToString } from "utils/imageHelpers";

const galleryThumbnailSvg = '/images/icons/gallery_thumbnail.svg';

interface IPrePasteProcessEvent {
    internal: boolean;
    content: string;
}

interface ISparrowTinyMceProps {
    value?: string;
    defaultLcid: string;
    activeLcid?: string;
    livePreview?: boolean;
    floatingToolbar?: boolean;
    loading?: boolean;
    disabled?: boolean;
    editorRef: React.MutableRefObject<any>;
    initOptions?: (RawEditorOptions & { selector?: undefined; target?: undefined; });
    customCss?: ICustomCssModelv1;
    enableInlineImages?: boolean; // enable inline images - opt out
    enableEmbeddedMedia?: boolean; // enable embedded media - opt out
    enablePageEmbed?: boolean;  // enable embedded media - opt out
    enableImageGallery?: boolean; // enable image gallery - opt out
    smartContentSuggestion?: string;
    smartContentEnabled?: boolean;
    changedSinceSaved?: boolean;
    onShowInlineImageDialog?: (editor) => void;
    onShowImageGalleryDialog?: (editor) => void;
    onEditImageGallery?: (editor) => void;
    onShowIntenseEmphasisDialog: (editor) => void;
    grabMentionUsers?: (filter: string) => any;
    onChange: (value?: string) => void;
    onFocus?: () => void;
    onFullscreen?: (isFullscreen?: boolean) => void;
    onShowSmartContentInsert?: (highlightedText: string, promptType: PromptTypeEnum) => void;
    clearSmartContentSuggestion?: () => void;
    generateAndOpenSmartContent?: (promptTypeForGeneration: PromptTypeEnum, promptTypeForInsert: PromptTypeEnum, body?: string) => void;
    personalizationTokens?: IPersonalizationToken[]; // Add merge tags / personalization tokens to enable for tinymce
    editorMaxWidth?: number; // set fixed max width to limit table and image sizes that are in the body
}
const DEFAULT_LINK_TARGET = "_blank";
const INTENSE_EMPHASIS_ARROW = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAQAAAD9CzEMAAAAo0lEQVR4Ae3WMQ3CQBxG8XZhrAAUFA2oQAW4ABXgAhP1gAN21i73GL+RhOQlXPh/tzb5DX25dviH1Wo17mwZvj+fH4EXJ0YTAFjYuQCsXNg4QPZg7wLQuDE5QPbkIAFZ4pWAxCsBiVcEEq8GJF4RSLwakHg7BhpXpk5f8srZzHRhNq+KI2Onl516XSfIzj6ZCVIBEqQAJEgL8H4dc34cKKCAAt5OnDwnCyae2QAAAABJRU5ErkJggg==";

const INTENSE_EMPHASIS_STYLE = {
    "background-color": "%color",
    "background-image": "url(" + INTENSE_EMPHASIS_ARROW + ")",
    "background-position": "right 10px center",
    "background-repeat": "no-repeat",
    "background-size": "24px 32px",
    "box-shadow": "-3px 3px 1px 0 rgba(212,212,212,1)",
    "border-radius": "3px",
    "color": "#ffffff",
    "font-weight": "600",
    "display": "inline-block",
    "padding": "15px 40px 15px 20px",
    "text-decoration": "none"
};

const EDIT_IMAGE_CORS_HOSTS: string[] = `${process.env.REACT_APP_EDIT_IMAGE_CORS_HOSTS}`.split(",");

const ALLOWED_BLOB_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

/**
 * A wrapper around @tinymce/tinymce-react for authoring
 */
const AuthoringTinyEditor: React.FunctionComponent<ISparrowTinyMceProps> = ({
    defaultLcid,
    livePreview,
    floatingToolbar,
    editorRef,
    activeLcid = defaultLcid,
    disabled = false,
    loading = false,
    initOptions,
    smartContentEnabled,
    enablePageEmbed = true, 
    enableEmbeddedMedia = false,
    enableInlineImages = false,
    enableImageGallery = false,
    customCss,
    smartContentSuggestion,
    changedSinceSaved = false,
    onShowInlineImageDialog,
    onShowImageGalleryDialog,
    onEditImageGallery,
    onShowIntenseEmphasisDialog,
    grabMentionUsers,
    onChange,
    onFullscreen,
    onFocus,
    onShowSmartContentInsert,
    clearSmartContentSuggestion,
    generateAndOpenSmartContent,
    personalizationTokens = [],
    editorMaxWidth,
    ...rest
}) => {
    const [spellcheckEnabled, setSpellcheckEnabled] = useState<boolean>(false);
    const [spellcheckLanguage, setSpellcheckLanguage] = useState<string>();
    const [value, setValue] = useState<string | undefined>(rest.value);

    // keep props/state in sync
    useEffect(() => {
        setValue(rest.value);
    }, [rest.value]);

    useEffect(() => {
        if(smartContentSuggestion && editorRef.current && clearSmartContentSuggestion) {
            editorRef.current.insertContent(smartContentSuggestion);
            clearSmartContentSuggestion();
        }
    }, [smartContentSuggestion]);

    const getSelectedText = (editor) => {
        return editor.selection.getContent({format: 'text'});
    }

    /**
     * Optional inline image functionality
     * @param editor
     */
    const setupInlineImages = (editor) => {
        if (!enableInlineImages || !onShowInlineImageDialog) return;

        editor.ui.registry.addMenuItem("inlineimage", {
            icon: "image",
            text: "Inline image",
            context: "insert",
            onAction: () => onShowInlineImageDialog(editor),
        });

        editor.ui.registry.addButton("inlineimage", {
            icon: "image",
            tooltip: "Insert inline image",
            onAction: () => onShowInlineImageDialog(editor)
        });
    }

    const setupImageGallery = (editor) => {
        if (!enableImageGallery || !onShowImageGalleryDialog || !onEditImageGallery) return;

        convertSVGToString(galleryThumbnailSvg).then((icon) => editor.ui.registry.addIcon("image-gallery", icon));

        editor.ui.registry.addMenuItem("imagegallery", {
            icon: "image-gallery",
            text: "Image Gallery",
            context: "Insert from image gallery",
            onAction: () => onShowImageGalleryDialog(editor),
        });

        editor.ui.registry.addButton("imagegallery", {
            icon: "image-gallery",
            tooltip: "Insert from image gallery",
            onAction: () => onShowImageGalleryDialog(editor),
        });

        editor.ui.registry.addButton("imagegalleryedit", {
            icon: "edit-block",
            tooltip: "Edit image gallery",
            onAction: () => onEditImageGallery(editorRef),
        });

        editor.ui.registry.addContextToolbar('imagegalleryedit', {
            predicate: (node: Element) => {
                const nodeId: string = node.id;
                return node.nodeName.toLowerCase() === 'div' && nodeId.includes('inline-gallery');
            },
            items: 'imagegalleryedit',
            position: 'node',
            scope: 'node'
        });
    }


    const setupEditorOnInit = (editor) => {
        editor.on('init', () => {
            editorRef.current = editor;
            if (editor) {
                changeLanguage();
            }
        });
    }

    const setupSmartContentMenuButton = (editor) => {
        if(smartContentEnabled) {
            editor.ui.registry.addIcon("magicIcon", magicIconWhiteSVG);
            /* Smart Content Dropdown Menu for quickbar*/
            editor.ui.registry.addMenuButton('smartContentQuickBarButton', {
                icon: 'magicIcon',
                text: ' ',
                fetch: (callback) => {
                    const items = createSmartContentHighlightedMenuButtons(editor);
                    callback(items);
                }
            });
            
            /* Smart Content Dropdown Menu for toolbar*/
            editor.ui.registry.addMenuButton('smartContentToolBarDropDown', {
                icon: 'magicIcon',
                text: ' ',
                fetch: (callback) => {
                    const items = createSmartContentMenuButtons();
                    callback(items);
                }
            });
        }
    }

    const setupFullScreenButton = (editor) => {
        editor.ui.registry.addButton("customfullscreen", {
            icon: "fullscreen",
            tooltip: "Fullscreen editor",
            onAction: () => {
                editor.execCommand("mceFullScreen");
                if (onFullscreen)
                    onFullscreen(editor.plugins.fullscreen.isFullscreen())
            }
        });
    }

    const setupIntenseEmphasisButton = (editor) => {
        editor.ui.registry.addButton("intenseEmphasis", {
            icon: "highlight-bg-color",
            tooltip: "Apply intense emphasis to link",
            disabled: true,
            onAction: () => onShowIntenseEmphasisDialog(editor),
            onSetup: (buttonApi) => {
                const handleIntenseEmphasis = (eventApi) => buttonApi.setEnabled(eventApi.element.nodeName.toLowerCase() === 'a');
                editor.off('NodeChange', handleIntenseEmphasis);
                return () => editor.on('NodeChange', handleIntenseEmphasis);
            }
        });
    }

    const setupMentionUsers = (editor) => {
        const onAction = function (autocompleteApi, rng, value) {
            editor.selection.setRng(rng);
            editor.insertContent(`<mark data-user-id="${value.id}" contenteditable="false" style="${MENTIONS_MARK_HIGHLIGHT_STYLE}">@${value.name}</mark> `);
            autocompleteApi.hide();
        }

        // optional mentions functionality
        if (grabMentionUsers)
            editor.ui.registry.addAutocompleter('specialchars', {
                ch: '@',
                minChars: 0,
                columns: 1,
                highlightOn: ['name_text', 'email_text'],
                onAction: onAction,
                fetch: async function (pattern) {
                    return await getMentionedUsers(pattern);
                }
            }
        );
    }

    const setupPersonalizationMenuItems = (editor) => {
        if (personalizationTokens.length > 0) {
            editor.ui.registry.addIcon("userAttributes", userAttributesSvg);

            editor.ui.registry.addMenuButton('customMergeTags', {
                icon: 'userAttributes',
                text: ' ',
                fetch: (callback) => {
                    const items = createPersonalizationTokensMenuButtons();
                    callback(items);
                }
            });

            editor.ui.registry.addNestedMenuItem('customMergeTags', {
                icon: 'userAttributes',
                text: 'Personalization token',
                getSubmenuItems: createPersonalizationTokensMenuButtons
            });
        }
    }

    const setupMaxImageWidth = (editor) => {
        if (editorMaxWidth) {
            const restrictImageToWidth = (img: HTMLImageElement) => {
                const currentWidth = parseInt(img.getAttribute('width') || `${img.offsetWidth}`, 10);
                const originalWidth = img.naturalWidth || currentWidth;
                const originalHeight = img.naturalHeight || img.offsetHeight;
                if (currentWidth > editorMaxWidth || (originalWidth > editorMaxWidth && img.getAttribute('width') === null)) {
                    const newWidth = editorMaxWidth;
                    // also maintain the same aspect ratio of the image
                    const newHeight = (originalHeight / originalWidth) * newWidth;
            
                    img.setAttribute('width', `${newWidth}`);
                    img.setAttribute('height', `${newHeight}`);
                    img.style.width = `${newWidth}px`;
                    img.style.height = `${newHeight}px`;
                    editor.fire("change");
                }
            }

            editor.on('NodeChange', () => {
                const images: NodeListOf<HTMLImageElement> = editor.getBody().querySelectorAll('img');
                images.forEach(img => {
                    img.onload = () => restrictImageToWidth(img);
                });
            });
        }
    }

    const setPastedLinksWithDefaultTarget = (editor) => {
        editor.on('paste', (e) => {
            setTimeout(() => {
                const links = editor.dom.select('a');
                links.forEach(link => {
                    if (!link.hasAttribute('target')) {
                        link.setAttribute('target', DEFAULT_LINK_TARGET);
                    }
                });
            });
        });
    }

    const onSetup = (editor) => {
        setupEditorOnInit(editor);
        setupInlineImages(editor);
        setupImageGallery(editor);
        setupFullScreenButton(editor);
        setupSmartContentMenuButton(editor);
        setupIntenseEmphasisButton(editor);
        setupMentionUsers(editor);
        setupPersonalizationMenuItems(editor);
        setupMaxImageWidth(editor);
        setPastedLinksWithDefaultTarget(editor);
    }

    useEffect(() => {
        if (editorRef.current) {
            changeLanguage();
        }
    }, [editorRef, activeLcid]);

    useEffect(() => {
        if (defaultLcid) {
            let spellcheckLanguage = defaultLcid.substring(0, 2).toLocaleLowerCase();
            let isEnabledNow = acceptableLanguages.includes(spellcheckLanguage.toLocaleLowerCase());

            setSpellcheckEnabled(isEnabledNow);
            setSpellcheckLanguage(spellcheckLanguage);
        }

    }, [defaultLcid]);

    // dynamically apply loading styles
    useEffect(() => {
        const findRuleToRemove = (sheet: CSSStyleSheet, rule: string): number | undefined => {
            let ruleToRemove: number | undefined = undefined;

            // find the rule to remove
            for (let i = 0; i < sheet.cssRules.length; i++) {
                let curr = sheet.cssRules[i];
                if (curr.cssText.includes(rule)) {
                    ruleToRemove = i;
                    break;
                }
            }

            return ruleToRemove;
        }

        if (editorRef && editorRef.current) {
            // grab our custom style sheet from the editor iframe (applied via content_css on init below)
            let sheet: CSSStyleSheet = [...editorRef.current.contentWindow.document.styleSheets]
                .find((sheet: CSSStyleSheet) => sheet.href?.includes(getContentCssUrl()));

            let ruleToRemove: number | undefined, ruleToAdd: string = "";

            // if loading, hide the content portion of the editor
            if (loading) {
                ruleToRemove = findRuleToRemove(sheet, ".mce-content-body { display: block; }");
                ruleToAdd = ".mce-content-body { display: none; }";
                // done loading, re-show the content portion of the editor
            } else {
                ruleToRemove = findRuleToRemove(sheet, ".mce-content-body { display: none; }");
                ruleToAdd = ".mce-content-body { display: block; }";
            }

            if (ruleToRemove !== undefined)
                sheet.deleteRule(ruleToRemove);

            // insert at index so that the import statement in tinyContentCss.css is always first
            sheet.insertRule(ruleToAdd, sheet.cssRules.length);
        }
    }, [loading, editorRef]);

    /**
     * Used to find the mentioned users.
     * @param pattern 
     * @returns mentioned users list.
     */
    const getMentionedUsers = async (pattern: string) => {
        if (grabMentionUsers) {
            const results = await grabMentionUsers(pattern);
    
            const userData = results.map(u => {
                const firstName = u.firstName || "[Unknown]";
                const lastName = u.lastName || "";
                const fullName = firstName + (lastName ? ` ${lastName}` : "");
                const initials = !firstName || !lastName ? "Un" : `${firstName[0]}${lastName[0]}`;
    
                return {
                    fullName,
                    email: u.email,
                    id: u.id,
                    color: u.color,
                    firstName,
                    lastName,
                    initials
                };
            });
    
            const mappedResults = userData.map(char => {
                const colorForAvatar = char.color ? char.color.substr(1) : "";
    
                return {
                    type: 'cardmenuitem',
                    value: { id: char.id, name: char.fullName === "[Unknown]" ? char.email : char.fullName },
                    label: char.fullName,
                    items: [{
                        type: 'cardcontainer',
                        direction: 'horizontal',
                        items: [{
                            type: 'cardtext',
                            text: char.initials,
                            name: 'name_image',
                            classes: ['mentions-icon-formatting', `avatar-color-${colorForAvatar}`]
                        }, {
                            type: 'cardcontainer',
                            direction: 'vertical',
                            items: [{
                                type: 'cardtext',
                                text: char.fullName,
                                name: 'name_text'
                            }, {
                                type: 'cardtext',
                                text: char.email,
                                name: 'email_text',
                                classes: ['mentions-email-formatting']
                            }]
                        }]
                    }]
                };
            }).slice(0, 6);
    
            return mappedResults;
        }
    }

    /**
     * Updates the spell check language.
     */
    const changeLanguage = (): void => {
        const currentSetLang = editorRef.current.plugins.tinymcespellchecker.getLanguage();
        if (currentSetLang !== activeLcid) {
            let languageToSet = activeLcid.substring(0, 2).toLocaleLowerCase();
            if (!acceptableLanguages.includes(languageToSet)) {
                languageToSet = "en";
                editorRef.current.execCommand('mceSpellcheckDisable');
            } else {
                editorRef.current.execCommand('mceSpellcheckEnable');
            }

            editorRef.current.plugins.tinymcespellchecker.setLanguage(languageToSet in lcidToHunspellLcid ? lcidToHunspellLcid[languageToSet] : languageToSet);
        }
    }

    /**
     * Creates the menu buttons for the smart content tool bar drop down. (scButton)
     */
    const createSmartContentMenuButtons = function () {
        //Makes sure that we do not include the <p></p> tags as part of the content.
        let buttonsEnabled = !(editorRef.current.getContent().replace(/<\/?p>/g, '').length <= 0);

        const menuItemsConfig = [
            {
                text: 'Generate alternate post body',
                promptType: PromptTypeEnum.Body,
                isEnabled: () => buttonsEnabled
            },
            {
                text: 'Generate summaries',
                promptType: PromptTypeEnum.Summarize,
                isEnabled: () => buttonsEnabled
            },
            {
                text: 'Generate titles',
                promptType: PromptTypeEnum.Title,
                isEnabled: () => buttonsEnabled
            },
        ];
    
        const createMenuItem = ({ text, promptType, isEnabled }) => ({
            type: 'menuitem',
            text,
            onAction: () => generateAndOpenSmartContent && generateAndOpenSmartContent(PromptTypeEnum.Body, promptType, editorRef.current.getContent()),
            onSetup: (api) => {
                api.setEnabled(isEnabled());
            }
        });
    
        return menuItemsConfig.map(createMenuItem);
    };

    /**
     * Creates the menu buttons for the smart content quick bar when highlighting text.
     * @param editor takes in the parameter editor to get the selected text.
     */
    const createSmartContentHighlightedMenuButtons = function (editor: any) {
        const menuActions = [
            { text: 'Rewrite', type: PromptTypeEnum.Rewrite },
            { text: 'Expand', type: PromptTypeEnum.Expand },
            { text: 'Shorten', type: PromptTypeEnum.Shorten },
            {
                text: 'Change Tone', type: 'nested', subMenu: [
                    { text: 'Friendly', type: PromptTypeEnum.Friendly },
                    { text: 'Professional', type: PromptTypeEnum.Professional },
                    { text: 'Witty', type: PromptTypeEnum.Witty },
                    { text: 'Heartfelt', type: PromptTypeEnum.Heartfelt },
                    { text: 'Educational', type: PromptTypeEnum.Educational },
                    { text: 'Gender Neutral', type: PromptTypeEnum.GenderNeutral }
                ]
            }
        ];
    
        const createAction = (text, actionType) => ({
            type: 'menuitem',
            text,
            onAction: () => onShowSmartContentInsert && onShowSmartContentInsert(getSelectedText(editor), actionType)
        });
    
        const createMenuItems = (actions) => actions.map(action => {
            if (action.type === 'nested') {
                return {
                    type: 'nestedmenuitem',
                    text: action.text,
                    getSubmenuItems: () => createMenuItems(action.subMenu)
                };
            } else {
                return createAction(action.text, action.type);
            }
        });
    
        return createMenuItems(menuActions);
    };
    
    /*
     * Adds a custom div into the menu item for personalization tokens to also display a styled description
     */
    const addCustomDescriptionsToPersonalizationTokens = (title: string, description: string) => {
        if (personalizationTokens.length > 0) {
            // grab the rendered div from the toolbar + dropdown insert menu where personalization tokens exist
            const menuItemDiv = document.querySelector(`div[title="${title}"][role="menuitem"]`) as HTMLDivElement;

            // sometimes the render is triggered mutliple times per menu item -- ensure that we're only adding the description once per node
            if (menuItemDiv.children.length === 1) {
                // create the description div
                let customDescriptionDiv = document.createElement('div');
                customDescriptionDiv.textContent = description;
                customDescriptionDiv.style.fontSize = "10px";
                customDescriptionDiv.style.color = "#7f7f7f";
                customDescriptionDiv.style.marginLeft = "4px";

                // append a copy of the div to both toolbar and dropdown menu items
                menuItemDiv.style.fontSize = "14px";
                menuItemDiv.style.color = "#333333";
                menuItemDiv.style.display = "flex";
                menuItemDiv.style.flexDirection = "column";
                menuItemDiv.style.alignItems = "flex-start";
                menuItemDiv.appendChild(customDescriptionDiv);
            }
        }
    }

    const getContentCssUrl = (): string => `${process.env.PUBLIC_URL}/tinyContentCss.css`;

    // for use in onPrePaste - TinyMCE does not support async functions on paste_preprocess and it
    // causes images_upload_handler to run before paste_preprocess is actually finished (if async)
    // so we must fetch the blob data synchronously 
    const fetchBlobDataSynchronously = (blobUrl: string): Blob | null => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', blobUrl, false);  // Synchronous request
      
        xhr.send();
      
        return xhr.status === 200 ? 
            base64toBlob(xhr.response, xhr.getResponseHeader('content-type')) :
            null;
    }

    const base64toBlob = (base64String: string, mimeType: string | null): Blob => {
        const byteCharacters = Buffer.from(base64String, 'base64');
        return new Blob([byteCharacters.buffer], { type: mimeType ?? 'image/*' });
    }

    /**
     * Process pasted content
     * - remove any images that are not blobs or from our blob storage
     */
    const onPrePaste = (editor: IEditor, args: IPrePasteProcessEvent) => {
        if (args.internal) return;

        // load content into a dom for easy parsing
        let temp = document.createElement("div");
        temp.innerHTML = args.content;

        // find all the img tags
        let imgs = temp.getElementsByTagName("img");

        let showErrorMessage = false;
        let showWrongFileTypeMessage = false;
        let allowedUrlRegex = /($\.jpg)|($\.png)|($\.jpeg)|(:image\/png)|(:image\/jpg)|(:image\/jpeg)/m;

        for (let i = 0; i < imgs.length; i++) {
            let curr = imgs[i];
            // for when a user drag and drops an image from their own computer, it enters as a blob:<window.location.origin>/<file_id>
            // so we transform it into a blob to get the mime-type to see if it's an image.
            // must fetch synchronously as paste_preprocess does not support async functions...
            let blob = fetchBlobDataSynchronously(curr.src);

            // validate url file extension or data/blob uri mime type
            showWrongFileTypeMessage = !(allowedUrlRegex.test(curr.src) || (blob && ALLOWED_BLOB_TYPES.includes(blob.type)));

            // if image is from our blob storage or a data/blob uri, it's allowed to be pasted and so we skip it
            if (
                !showWrongFileTypeMessage && (
                    EDIT_IMAGE_CORS_HOSTS.includes(curr.src) || // check if image is our blob host
                    /data:[\w/\-\.]+;\w+,.*/.test(curr.src) || // check if it's a data url
                    curr.src.includes(window.location.origin) // check if it's a blob uploaded via drag n drop
                )
            )
                continue;
            
            // remove the images that are not from our blob storage nor a data uri
            showErrorMessage = !showWrongFileTypeMessage;
            curr.remove();
        }

        if (showErrorMessage || showWrongFileTypeMessage) {
            editor.notificationManager.open({
                type: "error",
                text: showWrongFileTypeMessage
                    ? "File type not supported"
                    : `Images hosted externally are not allowed.`,
                closeButton: true,
                timeout: 6000
            });

            args.content = temp.innerHTML;
        }
    }

    /**
     * On drag image into editor, send upload request to our blob and return a sas url
     * - this sas url is then injected to the src of the image by tiny
     */
    const uploadImageHandler = async (blobInfo, progress) => {
        if (ALLOWED_BLOB_TYPES.includes(blobInfo.blob().type)) {
            try {
                let result = await ospreyApi.uploadImage(blobInfo);

                return result.sasUrl;
            } catch (err) {
                return Promise.reject({
                    remove: true,
                    message: "Please try again later."
                });
            }
        }
    }

    /* If any iframes with width/height attributes
     * apply those widths and heights via style attribute
     * - this solves the issue where the responsive styling for when viewing
     * a post overwrites the height attribute causing the video to shrink vertically.
     */
    const processIframes = (value: string): string => {
        let temp = document.createElement("div");
        temp.innerHTML = value;

        let iframes = temp.getElementsByTagName("iframe");

        let updated = false;

        for (let j = 0; j < iframes.length; j++) {
            let curr = iframes[j];

            let styleWidth = curr.style.width;
            let styleHeight = curr.style.height;

            if (curr.width && (styleWidth === undefined || styleWidth === null || styleWidth === "")) {
                curr.style.width = `${curr.width}px`;
                updated = true;
            }

            if (curr.height && (styleHeight === undefined || styleHeight === undefined || styleHeight === "")) {
                curr.style.height = `${curr.height}px`;
                updated = true;
            }
        }

        return updated ? temp.innerHTML : value;
    }

    // update local state and also propage event up tree
    const onBodyLiveChange = (newValue: string) => {
        newValue = processIframes(newValue);

        setValue(newValue);
        onChange(newValue);
    }

    // only update local state
    const onBodyLocalChange = (newValue: string) => {
        newValue = processIframes(newValue);

        //Enable "save as draft" button on first entry.
        if(!changedSinceSaved) {
            onBodyLiveChange(newValue);
        }

        setValue(newValue);
    }

    const onBodyChange = () => {
        if (editorRef.current?.isDirty()) 
            onChange(value)
    }

    const getContentCss = (): string[] => {
        let results = [getContentCssUrl()];

        if (customCss && customCss.sasUrl)
            results = [...results, customCss.sasUrl];

        return results;
    }

    const mediaUrlResolver = (data, resolve) => {
        const url = new URL(data.url);
        const urlParams = new URLSearchParams(url.search);

        // handle unlisted media urls manually (ones with an h query param)
        // otherwise, fallback to default url resolver by tinymce
        // the default resolver strips all query parameters for vimeo videos and replaces with some defaults
        // you can see this at node_modules/tinymce/plugins/media/plugin.js look for the urlPatterns array and scroll to the vimeo rules
        // here we keep the h parameter only and add the same defaults that tiny does
        const h = urlParams.get("h");

        if (h) {
            const sanitizedUrlParams = new URLSearchParams();
            sanitizedUrlParams.set("h", h);
            sanitizedUrlParams.set("title", "0");
            sanitizedUrlParams.set("byline", "0");

            const sanitizedUrl = `${url.protocol}//${url.hostname}${url.pathname}?${sanitizedUrlParams.toString()}`;

            const embedHtml = `<iframe src="${sanitizedUrl}" width="425" height="350"></iframe>`;
            resolve({ html: embedHtml });
        } else {
            resolve({ html: "" });
        }
    }

    /**
     * Creates the toolbar menu buttons adding our custom merge tags / personalization tokens
     */
    const createPersonalizationTokensMenuButtons = function () {
        if (personalizationTokens.length === 0) return [];

        const menuItems = personalizationTokens.map(token => ({
            type: "menuitem",
            text: token.title,
            onAction: () => editorRef.current.insertContent(`<span class="mce-mergetag" contenteditable="false" data-mce-cef-wrappable="true" data-mce-mergetag="1"><span class="mce-mergetag-affix" data-mce-mergetag-affix>${MERGE_TAG_PREFIX}</span>${token.value}<span class="mce-mergetag-affix" data-mce-mergetag-affix>${MERGE_TAG_SUFFIX}</span></span>`),
            onSetup: () => {
                addCustomDescriptionsToPersonalizationTokens(token.title, token.description);
            }
        }));
    
        return menuItems;
    };

    return (
        <>
            {loading &&
                <Loading containerStyle={{
                    position: "relative",
                    top: 216,
                    zIndex: 2,
                    marginBottom: -71,
                }} />}
            <Editor
                disabled={disabled}
                value={value}
                init={{
                    content_css: getContentCss(),
                    entity_encoding: "raw",
                    body_class: "body notranslate", // for custom tiny mce styles
                    max_width: editorMaxWidth,
                    content_style: `
                        .mce-content-body[data-mce-placeholder]:hover { 
                            cursor: text; 
                        }
                
                        ${
                            editorMaxWidth 
                                ? `
                                    img, table {
                                        max-width: ${editorMaxWidth}px;
                                    }
                                    table {
                                        table-layout: fixed;
                                        img {
                                            max-width: 100%;
                                        }
                                    }
                                    .wrap-pretext {
                                        white-space: pre-wrap;
                                        word-wrap: break-word;
                                    }
                                `
                                : "img { margin: 10px; }"
                        }
                    `,
                    formats: {
                        pre: { block: 'pre', classes: 'wrap-pretext' }
                    },
                    branding: false,
                    placeholder: "Start typing here...",
                    setup: (e) => onSetup(e),
                    convert_urls: false,
                    spellchecker_rpc_url: spellcheckerRpcUrl,
                    spellchecker_language: spellcheckLanguage,
                    spellchecker_active: spellcheckEnabled,
                    spellchecker_languages: process.env.REACT_APP_TINYMCE_SPELLCHECK_LANGS,
                    noneditable_noneditable_class: 'mceNonEditable',
                    styles: {
                        "intense-emphasis": { selector: "a", classes: "intenseEmphasis", styles: INTENSE_EMPHASIS_STYLE },
                    },
                    menubar: "edit insert format tools table",
                    menu: {
                        edit: { title: "Edit", items: "undo redo | selectall | searchreplace" },
                        insert: { title: "Insert", items: `link ${enableInlineImages ? "inlineimage" : ""} ${enableImageGallery ? "imagegallery" : ""} ${personalizationTokens.length > 0 ? "customMergeTags" : ""} ${enableEmbeddedMedia ? "media" : ""} | charmap hr | anchor` },
                        format: { title: "Format", items: "bold italic underline strikethrough superscript subscript | blocks fontfamily fontsize align | forecolor backcolor | removeformat" },
                        tools: { title: "Tools", items: "code" },
                        table: { title: "Table", items: "inserttable | cell row column | tableprops deletetable" }
                    },
                    plugins: [
                        "tinymcespellchecker", "a11ychecker", "anchor", "autolink", "autoresize", "charmap", "code", "link", "lists", "powerpaste", "searchreplace", "table", "advtable", "wordcount",
                        "quickbars", "advcode", "image", "editimage", "fullscreen", "advtemplate", ...(enableEmbeddedMedia ? ["media"] : []), ...(enablePageEmbed ? ["pageembed"] : []), ...(personalizationTokens.length > 0 ? ["mergetags"] : [])
                    ],
                    link_default_target: DEFAULT_LINK_TARGET,
                    link_target_list: [
                        { text: 'Current window', value: '_self' },
                        { text: 'New window', value: '_blank' }
                    ],
                    default_link_target: '_blank',
                    editimage_cors_hosts: EDIT_IMAGE_CORS_HOSTS,
                    editimage_toolbar: 'rotateleft rotateright | flipv fliph | editimage imageoptions',
                    image_advtab: true,
                    quickbars_insert_toolbar: "",
                    quickbars_selection_toolbar: 'bold italic | link h2 h3 blockquote | smartContentQuickBarButton',
                    powerpaste_word_import: "merge",
                    powerpaste_html_import: "merge",
                    paste_preprocess: onPrePaste,
                    images_upload_handler: uploadImageHandler,
                    images_reuse_filename: true,
                    toolbar: `undo redo | red | blocks | ${personalizationTokens.length > 0 ? "customMergeTags" : "" } smartContentToolBarDropDown bold italic underline forecolor backcolor | \
                alignleft aligncenter alignright alignjustify customfullscreen | pastetext bullist numlist outdent indent | \
                link unlink intenseEmphasis inlineimage ${enableImageGallery ? "imagegallery" : ""} ${enableEmbeddedMedia ? "media" : ""} ${enablePageEmbed ? "pageembed" : ""} | removeformat a11ycheck | spellchecker language | code inserttemplate addtemplate`,
                    toolbar_mode: floatingToolbar ? "floating" : "sliding",
                    font_family_formats: "Andale Mono=andale mono,times;" +
                        "Arial=arial,helvetica,sans-serif;" +
                        "Arial Black=arial black,avant garde;" +
                        "Book Antiqua=book antiqua,palatino;" +
                        "Comic Sans MS=comic sans ms,sans-serif;" +
                        "Courier New=courier new,courier;" +
                        "Georgia=georgia,palatino;" +
                        "Helvetica=helvetica;" +
                        "Impact=impact,chicago;" +
                        "Roboto=Roboto, sans-serif;" +
                        "Symbol=symbol;" +
                        "Tahoma=tahoma,arial,helvetica,sans-serif;" +
                        "Terminal=terminal,monaco;" +
                        "Times New Roman=times new roman,times;" +
                        "Trebuchet MS=trebuchet ms,geneva;" +
                        "Verdana=verdana,geneva;" +
                        "Webdings=webdings;" +
                        "Wingdings=wingdings,zapf dingbats",
                    advtemplate_list: async () => {
                        return await TinyMceTemplatesService.getCategories({ excludeInlineImages: !enableInlineImages, excludeEmbeddedMedia: !enableEmbeddedMedia });
                    },
                    advtemplate_get_template: TinyMceTemplatesService.getTemplate,
                    advtemplate_create_category: TinyMceTemplatesService.createCategory,
                    advtemplate_create_template: TinyMceTemplatesService.createTemplate,
                    advtemplate_rename_category: TinyMceTemplatesService.renameCategory,
                    advtemplate_rename_template: TinyMceTemplatesService.renameTemplate,
                    advtemplate_delete_category: TinyMceTemplatesService.deleteCategory,
                    advtemplate_delete_template: TinyMceTemplatesService.deleteTemplate,
                    advtemplate_move_template: TinyMceTemplatesService.moveTemplate,
                    advtemplate_move_category_items: TinyMceTemplatesService.moveCategoryItems,
                    media_url_resolver: mediaUrlResolver,
                    video_template_callback: (data) => {
                        let result = `<video width="${data.width}" height="${data.height}"${data.poster ? ` poster="${data.poster}"` : ''} controls="controls">\n` +
                            `<source src="${data.source}"${data.sourcemime ? ` type="${data.sourcemime}"` : ''} />\n` +
                            (data.altsource ? `<source src="${data.altsource}"${data.altsourcemime ? ` type="${data.altsourcemime}"` : ''} />\n` : '') +
                            '</video>';

                        if (data.source.includes("wistia.net/embed/"))
                            result = `<p>
                                <iframe allowfullscreen="allowfullscreen" src="${data.source}" name="wistia_embed" width="560" height="315"></iframe>
                            </p>`;
                        else if (data.source.includes("players.brightcove.net"))
                            result = `<p>
                                <iframe allowfullscreen="allowfullscreen" src="${data.source}" width="560" height="315"></iframe>
                            </p>`;

                        return result;
                    },
                    mergetags_prefix: MERGE_TAG_PREFIX,
                    mergetags_suffix: MERGE_TAG_SUFFIX,
                    mergetags_list: personalizationTokens,
                    ...initOptions
                }}
                onEditorChange={livePreview ? onBodyLiveChange : onBodyLocalChange}
                onBlur={onBodyChange}
                onFocus={onFocus}
            />
        </>
    );
}

export { AuthoringTinyEditor };
